import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
    useLazyGetBrandElementsQuery,
    useSaveScrapingURLMutation,
} from '../../store/api/brandMagic.api';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { displayErrorToast } from '../../utils/ToastUtils';
import { ensureHttpInUrl } from '../../utils/common.utils';
import { getEmptySession } from '../../utils/session.utils';
import { getSessions } from '../../store/selector/session.selector';
import { useCreateNewSessionMutation } from '../../store/api/session.api';
import { AddSession } from '../../store/slice/session.slice';
import { SetSelectedCampaign } from '../../store/slice/campaign.slice';
import { useNavigate } from 'react-router-dom';
import { SetAdFromUrlActive, SetAdFromUrlLoading } from '../../store/slice/ad-from-url.slice';
import config from '../../config';
import { APP_ENVIRONMENT_PRODUCTION } from '../../constants/app';
import { IGetBrandElements } from '../../interfaces/brand-magic.interface';
import { SetDesignElements } from '../../store/slice/brand-elements.slice';

const AdFromUrlDashboard = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const sessions = useAppSelector(getSessions);
    const activeBrand = useAppSelector(getActiveBrand);
    const [fetchBrandData] = useLazyGetBrandElementsQuery();
    const [saveScrapingURL] = useSaveScrapingURLMutation();
    const [createNewSession] = useCreateNewSessionMutation();

    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState('');

    const handleUrlSubmit = async () => {
        if (!activeBrand) return;
        if (url === '') {
            displayErrorToast('Please enter a URL');
            return;
        }
        const urlRegex =
            /^(http(s)?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/;
        if (!urlRegex.test(url)) {
            displayErrorToast('Please enter a valid URL');
            return;
        }

        setLoading(true);
        const correctedUrl = ensureHttpInUrl(url);
        try {
            await saveScrapingURL({
                url: correctedUrl?.trim(),
                brandId: activeBrand.id,
                isAdFromURLFlow: config.env === APP_ENVIRONMENT_PRODUCTION ? true : false,
            });

            const brandDataResponse = await fetchBrandData(activeBrand.id);
            if (brandDataResponse.data) {
                const data: IGetBrandElements = brandDataResponse.data;
                dispatch(
                    SetDesignElements({
                        fonts: data.fonts,
                        colors: data.colors,
                        logo: data.logo,
                    }),
                );
            }
        } catch (e) {
            displayErrorToast('Failed to read the URL. Please try again.');
            setLoading(false);
            return;
        }

        let session = getEmptySession(sessions);
        if (!session) {
            const response = await createNewSession(activeBrand.id);
            if (response && 'data' in response) {
                session = response.data;
                // session = { ...session, stage: SessionStage.CREATE };
                dispatch(AddSession(session));
            }
        }
        if (session) {
            dispatch(SetSelectedCampaign(null));
            dispatch(SetAdFromUrlActive(true));
            dispatch(SetAdFromUrlLoading(true));
            navigate(`/campaign?q=${session.sessionKey}`);
        } else {
            displayErrorToast('Some error occured. Please try again');
        }

        setLoading(false);
    };

    return (
        <div className="tw-h-full tw-min-w-sm tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-12 tw-px-5">
            <div className="tw-flex tw-flex-col tw-gap-y-4">
                <div className="tw-font-semibold tw-text-lg lg:tw-text-5xl tw-text-center">
                    What’s your website?
                </div>
                <div className="tw-font-medium tw-text-sm lg:tw-text-xl tw-text-center">
                    I’ll read it and learn about your brand.
                </div>
            </div>
            <div className="tw-w-full tw-max-w-[900px] tw-flex tw-flex-col tw-gap-y-4">
                <div className="tw-w-full">
                    <Form.Control
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder={'Your website URL'}
                        className="no-scroll tw-w-full tw-overflow-hidden tw-px-3 tw-py-3 tw-shadow"
                        required={false}
                        autoFocus={true}
                    />
                </div>
                <div className="tw-w-full tw-flex tw-items-start tw-justify-between">
                    <div className="tw-text-base tw-font-semibold">
                        {loading ? <>Learning your brand...</> : <></>}
                    </div>
                    <Button variant="primary" onClick={handleUrlSubmit} disabled={loading}>
                        Try this
                        {loading && <Spinner as="span" className="tw-w-6 tw-h-6" />}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AdFromUrlDashboard;
